/**
 * Service quản lý đồng bộ trạng thái đọc & tìm kiếm lên URL (Deep Linking & Sharing)
 * Định dạng URL chuẩn:
 * 👉 ?source=s1&story=ten-truyen&chap=chuong-1
 * Người nhận mở link sẽ biết chính xác đọc truyện nào, chương nào và từ adapter nào!
 */
import { DEFAULT_SOURCE, ADAPTER_SOURCES } from '../config/constants.js';

export class UrlService {
  /**
   * Đọc các tham số hiện tại từ URL trình duyệt
   * @returns {{ source: string, storyId: string|null, chapterId: string|null, q: string|null }}
   */
  static getParams() {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const hash = window.location.hash || '';
      const queryIndex = hash.indexOf('?');
      const hashParams = new URLSearchParams(queryIndex >= 0 ? hash.slice(queryIndex + 1) : '');
      const getParam = (...keys) => {
        for (const key of keys) {
          const value = hashParams.get(key) || searchParams.get(key);
          if (value) return value;
        }
        return null;
      };

      let source = getParam('source', 's') || DEFAULT_SOURCE;
      let storyId = getParam('story', 'story_id');
      let chapterId = getParam('chap', 'chapter', 'chapter_id');
      const q = getParam('q', 'search');

      // Tự động bóc tách nếu ai đó gửi link cũ
      const oldUrl = getParam('url');
      if (oldUrl && oldUrl.startsWith('http')) {
        const m = oldUrl.match(/https?:\/\/[^\/]+\/([^\/]+)\/([^\/]+)\/?/);
        if (m) {
          storyId = m[1];
          chapterId = m[2];
        }
      }

      return {
        source,
        storyId: storyId || null,
        chapterId: chapterId || null,
        q: q || null,
      };
    } catch (err) {
      console.warn('Lỗi phân tích URL params:', err);
      return { source: DEFAULT_SOURCE, storyId: null, chapterId: null, q: null };
    }
  }

  /**
   * Xác định trang web hiện tại có đang được yêu cầu mở chế độ Video không
   * @returns {boolean} true nếu là liên kết video / YouTube
   */
  static isVideoUrl() {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const mode = searchParams.get('mode');
      const theme = searchParams.get('theme');
      const videoId = searchParams.get('v') || searchParams.get('video') || searchParams.get('yt');
      const rawUrl = searchParams.get('url') || '';

      // 1. Nếu có chỉ định mode=video rõ ràng
      if (mode === 'video') return true;

      // 2. Nếu có tham số video ID
      if (videoId) return true;

      // 3. Nếu theme được chọn là các giao diện video editor
      const videoThemes = ['capcut', 'editor', 'davinci', 'premiere', 'canva', 'canva-video'];
      if (theme && videoThemes.includes(theme.toLowerCase())) return true;

      // 4. Nếu link url truyền vào là YouTube
      if (rawUrl.includes('youtube.com') || rawUrl.includes('youtu.be')) return true;

      return false;
    } catch (err) {
      console.warn('Lỗi kiểm tra isVideoUrl:', err);
      return false;
    }
  }

  /**
   * Lấy toàn bộ tham số chuyên dụng cho chế độ Video
   * @returns {{ videoId: string|null, theme: string, time: number, query: string|null }}
   */
  static getVideoParams() {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      
      // 1. Bóc tách videoId từ các param khả dĩ
      let videoId = searchParams.get('v') || searchParams.get('video') || searchParams.get('yt');
      const rawUrl = searchParams.get('url');
      if (!videoId && rawUrl) {
        const match = rawUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
        if (match && match[1]) {
          videoId = match[1];
        }
      }

      // 2. Theme giao diện ngụy trang video (ưu tiên param URL, nếu không có đọc từ localStorage)
      let rawTheme = searchParams.get('theme');
      if (!rawTheme) {
        try {
          rawTheme = localStorage.getItem('fake_video_theme') || localStorage.getItem('fake_disguise_theme');
        } catch (e) {}
      }

      let theme = (rawTheme || 'capcut').toLowerCase();
      if (theme === 'editor' || theme === 'premiere') theme = 'davinci';
      if (theme === 'canva-video' || theme === 'canvan') theme = 'canva';
      if (!['capcut', 'davinci', 'canva'].includes(theme)) {
        theme = 'capcut';
      }

      // 3. Thời gian bắt đầu video (giây)
      const rawTime = searchParams.get('t') || searchParams.get('time') || '0';
      let time = parseInt(rawTime, 10);
      if (isNaN(time) || time < 0) time = 0;

      // 4. Từ khóa tìm kiếm media (nếu có)
      const query = searchParams.get('vq') || searchParams.get('q') || null;

      return {
        videoId: videoId ? decodeURIComponent(videoId) : null,
        theme: theme,
        time: time,
        query: query ? decodeURIComponent(query) : null
      };
    } catch (err) {
      console.warn('Lỗi đọc video params:', err);
      return { videoId: null, theme: 'capcut', time: 0, query: null };
    }
  }

  /**
   * Cập nhật URL trình duyệt cho chế độ Video (giữ đồng bộ trạng thái khi chia sẻ)
   * @param {Object} options
   * @param {string} [options.videoId] Mã video ID
   * @param {string} [options.theme] Giao diện fake ('capcut' | 'davinci' | 'canva')
   * @param {number} [options.time] Thời gian hiện tại của video (giây)
   * @param {string} [options.query] Từ khóa tìm footage
   * @param {boolean} [pushState=false] true: thêm vào lịch sử, false: thay thế URL hiện tại
   */
  static setVideoParams({ videoId, theme, time, query } = {}, pushState = false) {
    try {
      const currentUrl = new URL(window.location.href);

      // Thiết lập cờ chế độ video
      currentUrl.searchParams.set('mode', 'video');

      // Nếu đang dùng Hash Router (#/...), xóa tham số theme khỏi query để không bị kẹt như ?theme=zalo#/capcut
      if (window.location.hash && window.location.hash.startsWith('#/')) {
        currentUrl.searchParams.delete('theme');
      } else if (theme) {
        currentUrl.searchParams.set('theme', theme);
        try {
          localStorage.setItem('fake_video_theme', theme);
          localStorage.setItem('fake_disguise_theme', theme);
        } catch (e) {}
      }

      // Đồng bộ cả query và hash: router ưu tiên hash, nên không để video
      // hoặc thời gian cũ trong #/capcut?v=...&t=... sau khi chọn kết quả mới.
      const videoParamSets = [currentUrl.searchParams];
      let hashRoute;
      let hashParams;
      if (currentUrl.hash.startsWith('#/')) {
        const queryIndex = currentUrl.hash.indexOf('?');
        hashRoute = queryIndex === -1 ? currentUrl.hash : currentUrl.hash.slice(0, queryIndex);
        hashParams = new URLSearchParams(queryIndex === -1 ? '' : currentUrl.hash.slice(queryIndex + 1));
        videoParamSets.push(hashParams);
      }

      for (const params of videoParamSets) {
        ['v', 'video', 'videoId', 'yt', 't', 'time'].forEach(key => params.delete(key));
        if (videoId) params.set('v', videoId);
        if (Number.isFinite(time) && time > 0) {
          params.set('t', Math.floor(time).toString());
        }
      }
      if (hashParams) {
        const hashQuery = hashParams.toString();
        currentUrl.hash = hashRoute + (hashQuery ? `?${hashQuery}` : '');
      }

      if (query && query.trim()) {
        currentUrl.searchParams.set('vq', query.trim());
      } else {
        currentUrl.searchParams.delete('vq');
      }

      // Dọn dẹp các param của truyện để URL ngắn gọn và sạch sẽ
      currentUrl.searchParams.delete('story');
      currentUrl.searchParams.delete('chap');
      currentUrl.searchParams.delete('source');
      currentUrl.searchParams.delete('url');

      const stateObj = { mode: 'video', videoId, theme, time };
      if (pushState) {
        window.history.pushState(stateObj, '', currentUrl.toString());
      } else {
        window.history.replaceState(stateObj, '', currentUrl.toString());
      }
    } catch (err) {
      console.warn('Lỗi cập nhật URL video:', err);
    }
  }

  /**
   * Làm sạch các tham số query giao diện cũ (?theme=..., ?mode=...) khi chuyển sang Hash Route
   * Đảm bảo URL sạch đẹp: http://localhost:3000/#/capcut thay vì http://localhost:3000/?theme=zalo#/capcut
   */
  static cleanAppQueryParams() {
    try {
      const currentUrl = new URL(window.location.href);
      let changed = false;
      const keysToRemove = ['theme', 'mode', 'v', 'video', 'videoId', 'yt', 'play', 't', 'time', 'vq'];
      keysToRemove.forEach(k => {
        if (currentUrl.searchParams.has(k)) {
          currentUrl.searchParams.delete(k);
          changed = true;
        }
      });
      if (changed) {
        window.history.replaceState(window.history.state || {}, '', currentUrl.toString());
      }
    } catch (err) {
      console.warn('Lỗi khi làm sạch query params:', err);
    }
  }

  /**
   * Xóa bỏ hoàn toàn các tham số của chế độ Video trên URL (mode=video, v, video, yt, time, vq...)
   * Giúp quay về URL đọc truyện sạch sẽ, ngăn ngừa việc F5 bị kích hoạt lại video
   * @param {string} [targetTheme='sheets'] Theme truyện đích ('sheets' | 'email' | 'zalo')
   */
  static cleanVideoParams(targetTheme = 'sheets') {
    try {
      const currentUrl = new URL(window.location.href);

      // Bước 1: Xóa triệt để cờ mode=video để hệ thống không nhận nhầm sang video khi F5
      currentUrl.searchParams.delete('mode');

      // Bước 2: Xóa tất cả các tham số video YouTube
      currentUrl.searchParams.delete('v');
      currentUrl.searchParams.delete('video');
      currentUrl.searchParams.delete('videoId');
      currentUrl.searchParams.delete('yt');
      currentUrl.searchParams.delete('play');
      currentUrl.searchParams.delete('time');
      currentUrl.searchParams.delete('t');
      currentUrl.searchParams.delete('vq');

      // Bước 3: Nếu đang dùng Hash Router, không gán theme vào query search để tránh kẹt query
      if (window.location.hash && window.location.hash.startsWith('#/')) {
        currentUrl.searchParams.delete('theme');
      } else if (targetTheme) {
        currentUrl.searchParams.set('theme', targetTheme);
      }

      // Bước 4: Cập nhật URL sạch lên thanh địa chỉ trình duyệt mà không tải lại trang
      window.history.replaceState({ mode: 'story', theme: targetTheme }, '', currentUrl.toString());
    } catch (err) {
      console.warn('Lỗi khi dọn dẹp tham số video trên URL:', err);
    }
  }

  /**
   * Cập nhật URL khi đang đọc một chương truyện (ĐƯA ĐẦY ĐỦ source, story, chap LÊN URL)
   * @param {string} source Mã nguồn adapter (ví dụ: 's1')
   * @param {string} storyId Slug truyện
   * @param {string} chapterId Slug chương
   * @param {boolean} pushState true: tạo lịch sử mới, false: thay thế URL
   */
  static setReading(source, storyId, chapterId, pushState = true) {
    if (!storyId || !chapterId) return;
    const src = source || DEFAULT_SOURCE;

    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('source', src);
      currentUrl.searchParams.set('story', storyId);
      currentUrl.searchParams.set('chap', chapterId);
      
      // Xóa các param cũ nếu có
      currentUrl.searchParams.delete('chapter');
      currentUrl.searchParams.delete('url');
      currentUrl.searchParams.delete('s');

      // Giữ nguyên param q nếu modal tìm kiếm đang mở, ngược lại xóa q
      const modal = document.getElementById('search-modal');
      const isModalOpen = modal && modal.style.display === 'flex';
      if (!isModalOpen) {
        currentUrl.searchParams.delete('q');
        currentUrl.searchParams.delete('search');
      }

      if (currentUrl.hash.startsWith('#/')) {
        const queryIndex = currentUrl.hash.indexOf('?');
        if (queryIndex >= 0) {
          const hashRoute = currentUrl.hash.slice(0, queryIndex);
          const hashParams = new URLSearchParams(currentUrl.hash.slice(queryIndex + 1));
          ['source', 's', 'story', 'story_id', 'chap', 'chapter', 'chapter_id'].forEach(key => {
            hashParams.delete(key);
          });
          hashParams.set('source', src);
          hashParams.set('story', storyId);
          hashParams.set('chap', chapterId);
          if (!isModalOpen) {
            hashParams.delete('q');
            hashParams.delete('search');
          }
          currentUrl.hash = `${hashRoute}?${hashParams.toString()}`;
        }
      }

      const stateObj = { type: 'reading', source: src, storyId, chapterId };
      if (pushState) {
        window.history.pushState(stateObj, '', currentUrl.toString());
      } else {
        window.history.replaceState(stateObj, '', currentUrl.toString());
      }
    } catch (err) {
      console.warn('Lỗi cập nhật URL reading:', err);
    }
  }

  /**
   * Cập nhật URL khi đang tìm kiếm truyện (kèm mã nguồn adapter)
   * @param {string} keyword Từ khóa tìm kiếm
   * @param {string} source Mã nguồn adapter
   * @param {boolean} pushState 
   */
  static setSearch(keyword, source = DEFAULT_SOURCE, pushState = true) {
    const cleanKey = (keyword || '').trim();
    if (!cleanKey) return;
    const src = source || DEFAULT_SOURCE;

    try {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('source', src);
      currentUrl.searchParams.set('q', cleanKey);

      const stateObj = { type: 'search', source: src, keyword: cleanKey };
      if (pushState) {
        window.history.pushState(stateObj, '', currentUrl.toString());
      } else {
        window.history.replaceState(stateObj, '', currentUrl.toString());
      }
    } catch (err) {
      console.warn('Lỗi cập nhật URL search:', err);
    }
  }

  /**
   * Xóa từ khóa tìm kiếm khỏi URL khi đóng modal
   */
  static clearSearch() {
    try {
      const currentUrl = new URL(window.location.href);
      let changed = false;
      if (currentUrl.searchParams.has('q') || currentUrl.searchParams.has('search')) {
        currentUrl.searchParams.delete('q');
        currentUrl.searchParams.delete('search');
        changed = true;
      }

      if (currentUrl.hash.startsWith('#/')) {
        const queryIndex = currentUrl.hash.indexOf('?');
        if (queryIndex >= 0) {
          const route = currentUrl.hash.slice(0, queryIndex);
          const hashParams = new URLSearchParams(currentUrl.hash.slice(queryIndex + 1));
          if (hashParams.has('q') || hashParams.has('search')) {
            hashParams.delete('q');
            hashParams.delete('search');
            currentUrl.hash = hashParams.size ? `${route}?${hashParams}` : route;
            changed = true;
          }
        }
      }

      if (changed) window.history.replaceState(window.history.state, '', currentUrl.toString());
    } catch (err) {
      console.warn('Lỗi xóa search param:', err);
    }
  }

  /**
   * Sao chép đường dẫn chia sẻ sạch vào Clipboard
   * @returns {Promise<boolean>}
   */
  static async copyShareLink() {
    try {
      const link = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
        return true;
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
      }
    } catch (err) {
      console.error('Không thể sao chép liên kết:', err);
      return false;
    }
  }
}
